import { useEffect, useState } from 'react';

import { cn } from '../../lib/cn';
import styles from './Avatar.module.css';

export type AvatarSize = 'xs' | 'sm' | 'md';

export type AvatarUser = {
  readonly id: string;
  readonly login: string;
  readonly displayName: string;
  readonly avatarColor?: string;
  /** Загруженное фото; `null`/`undefined` — инициалы на `avatarColor`. */
  readonly avatarUrl?: string | null;
  readonly isActive?: boolean;
};

export type AvatarProps = {
  readonly user: AvatarUser | null;
  readonly size?: AvatarSize;
  readonly showTooltip?: boolean;
};

/** Фото, если загружено; иначе инициалы на `avatarColor` (docs/06 §4). */
export function Avatar({ user, size = 'sm', showTooltip = true }: AvatarProps) {
  const [isPhotoBroken, setPhotoBroken] = useState(false);

  useEffect(() => {
    setPhotoBroken(false);
  }, [user?.avatarUrl]);

  if (!user) {
    return (
      <span
        className={cn(styles['avatar'], styles[size], styles['empty'])}
        title="Без исполнителя"
        aria-label="Без исполнителя"
      >
        ?
      </span>
    );
  }

  const initials = toInitials(user.displayName.length > 0 ? user.displayName : user.login);
  const background = user.avatarColor ?? 'var(--c-done)';
  const photo = user.avatarUrl && !isPhotoBroken ? user.avatarUrl : null;

  return (
    <span
      className={cn(styles['avatar'], styles[size], user.isActive === false && styles['inactive'])}
      style={{ background }}
      title={showTooltip ? `${user.displayName} (@${user.login})` : undefined}
      aria-label={user.displayName}
    >
      {photo === null ? (
        initials
      ) : (
        <img className={styles['photo']} src={photo} alt="" onError={() => setPhotoBroken(true)} />
      )}
    </span>
  );
}

export function toInitials(value: string): string {
  const parts = value.trim().split(/\s+/).filter((part) => part.length > 0);
  const first = parts[0];
  if (!first) {
    return '?';
  }
  const second = parts[1];
  if (second) {
    return `${first[0]}${second[0]}`.toUpperCase();
  }
  return first.slice(0, 2).toUpperCase();
}
